import { useState, useEffect } from 'react';
import { FREQ_LABELS, resolveShiftForDay } from '../../lib/workerData';
import { fetchChecklist, getFilteredTasks } from '../../lib/checklists';
import { s } from '../../lib/workerStyles';

// Buildings whose task list is shown on the worker's phone during a shift.
// Switched on for PDIC (E) and RPYCC (F) only, per Fabian 2026-10-20. Add a
// building id here to roll the list out to it; nothing else needs to change.
export const TASK_LIST_BUILDINGS = ['E', 'F'];

// Day shifts at E/F do the twice-daily rounds; the night shift does the full
// clean. 'daily' is included because that is what the portal's Building Tasks
// editor gives a new task by default, so a task Fabian adds isn't silently
// hidden from the crew.
function shiftFreqs(profile) {
  const shift = resolveShiftForDay(profile);
  if (shift === 'day1' || shift === 'day2') return ['twice-daily', 'daily'];
  return ['nightly', 'twice-daily', 'daily'];
}

// A read-and-tick list shown under the on-shift card. Ticks are kept on the
// phone only (per shift) and are never required for, or sent with, check-out:
// sign-in and sign-out behave exactly as they do without this list.
export default function ShiftTaskList({ building, profile, shiftId }) {
  const storageKey = 'tc_tasks_' + (shiftId || 'none') + '_' + building.id;
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // The combined E building carries the youth centre's (F) tasks as well.
    const load = building.id === 'E'
      ? Promise.all([fetchChecklist('E'), fetchChecklist('F')]).then(([e, f]) => (e || f) ? { zones: [...(e?.zones || []), ...(f?.zones || [])] } : null)
      : fetchChecklist(building.id);
    load.then(result => {
      if (!cancelled) { setChecklist(result); setLoading(false); }
    }).catch(() => { if (!cancelled) { setChecklist(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [building.id]);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(checked)); } catch { /* storage full or blocked */ }
  }, [checked, storageKey]);

  if (loading) {
    return <div style={{ color: '#64748b', fontSize: 13, padding: '16px 0' }}>Loading tasks…</div>;
  }
  if (!checklist || !checklist.zones?.length) return null;

  const zones = showAll
    ? checklist.zones.filter(z => z.tasks.length > 0)
    : getFilteredTasks(checklist.zones, shiftFreqs(profile));
  const all = zones.flatMap(z => z.tasks);
  const done = all.filter(t => checked[t.id]).length;
  const toggle = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>TODAY'S TASKS</div>
        <div style={{ color: '#64748b', fontSize: 12 }}>{done}/{all.length} done</div>
      </div>
      {zones.length === 0 && (
        <div style={s.emptyState}>No tasks for this shift.</div>
      )}
      {zones.map((zone, zi) => {
        const zoneDone = zone.tasks.filter(t => checked[t.id]).length;
        return (
          <div key={zi} style={{ ...s.zone, ...(zoneDone === zone.tasks.length ? s.zoneDone : {}) }}>
            <div style={s.zoneHeader}>
              <div style={s.zoneName}>{zone.name}</div>
              <span style={s.zoneDoneCount}>{zoneDone}/{zone.tasks.length}</span>
            </div>
            {zone.note && <div style={s.zoneNote}>{zone.note}</div>}
            <div style={s.taskList}>
              {zone.tasks.map(task => (
                <button key={task.id} style={s.taskRow(!!checked[task.id])} onClick={() => toggle(task.id)}>
                  <div style={s.taskCheck(!!checked[task.id])}>{checked[task.id] && '✓'}</div>
                  <div style={s.taskBody}>
                    <div style={s.taskLabel(!!checked[task.id])}>{task.label}</div>
                    {showAll && FREQ_LABELS[task.freq] && (
                      <div style={{ fontSize: 11, marginTop: 2, color: FREQ_LABELS[task.freq].color }}>{FREQ_LABELS[task.freq].label}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <button style={s.maintFlagBtn} onClick={() => setShowAll(v => !v)}>
        {showAll ? 'Show this shift’s tasks only' : 'Show all tasks (weekly, monthly…)'}
      </button>
    </div>
  );
}
